<template>
  <div
    v-if="isValid"
    class="DirectView a-cover overflow-hidden flex"
    :class="wrapperClasses"
  >
    <div class="relative w-full max-h-full overflow-auto scrolling-touch">
      <!-- Fixed Header Image -->
      <div
        v-if="headerImageUrl"
        class="relative w-full bg-full"
        style="padding-bottom: 22.77%;"
        :style="{
          paddingBottom: `${headerImageRatio * 100}%`,
          backgroundImage: `url('${headerImageUrl}')`,
        }"
      />
      <!-- Copy -->
      <div class="relative w-full max-w-270 mx-auto pt-6 md:pt-11 pb-10 md:pb-15">
        <!-- Text Section -->
        <div class="max-w-170 mb-8 md:mb-12 last:mb-0 px-2_5">
          <Heading
            v-if="headingText"
            class="mb-5 md:mb-6 last:mb-0"
            large
            :text="headingText"
          />
          <div
            v-if="pageObj.body"
            class="tiny-mce mb-7 last:mb-0"
            v-html="pageObj.body"
          />
          <div
            v-if="mainCallToActionObj"
          >
            <MultiBtn
              :url="utmHref(mainCallToActionObj.url, 'tier-2-cta')"
              :text="mainCallToActionObj.btn_text"
              :label="mainCallToActionObj.btn_title"
              :icon="mainCallToActionObj.btn_icon || 'external-link'"
              :theme="mainCallToActionObj.button_style || 'button_style_1'"
            />
          </div>
        </div>
        <!-- Sections -->
        <div
          v-if="usedArr( sectionsArr )"
        >
          <template
            v-for="(sectionObj, i1) in sectionsArr"
          >
            <div
              v-if="usedArr( sectionObj.cards )"
              :key="`direct-${routeDirectPage.slug}-${i1}`"
              class="mb-10 last:mb-0"
            >
              <Subheading
                v-if="sectionObj.subheading_text"
                class="mb-3 lg:mb-7_5 last:mb-0 px-2_5"
                :text="sectionObj.subheading_text"
              />
              <ol class="sm:flex items-center flex-wrap">
                <li
                  v-for="(cardObj, i2) in sectionObj.cards"
                  :key="`direct-${routeDirectPage.slug}-${i1}-${i2}`"
                  class="w-1/1 sm:w-1/2 md:w-1/3 p-2_5"
                  :class="`lg:w-${cardObj.card_width || '1/4'}`"
                >
                  <DirectCard
                    :post-obj="cardObj.post"
                    :large-size="'1/4' !== cardObj.card_width"
                  />
                </li>
              </ol>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Heading from '@/components/text/Heading.vue'
import Subheading from '@/components/text/Subheading.vue'
import MultiBtn from '@/components/buttons/MultiBtn.vue'
import DirectCard from '@/components/buttons/DirectCard.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'Direct',

  components: {
    Heading,
    Subheading,
    MultiBtn,
    DirectCard,
  },

  data() {
    return {
      headerImageRatio: 0.2277,
    }
  },

  computed: {
    // Data
    ...mapGetters([
      'directPagesEnabled',
      'routeDirectPage',
      'myStore',
    ]),
    pageObj() { return this.routeDirectPage || {} },
    globalStyles() { return this.myStore(['styles', 'global']) ?? {} },
    directPageStyles() { return this.myStore(['styles', 'direct_page_screen']) ?? {} },
    // Container
    wrapperClasses() {
      const { globalStyles, directPageStyles } = this
      const globalPageStyles = globalStyles.page ?? {}
      // Background Color
      const bgColorVal = directPageStyles.bg_color || globalStyles.bg_color || 'off-white'
      const bgColorClass = `bg-${bgColorVal}`
      // Text Color
      const textColorVal = directPageStyles.text_color || globalPageStyles.text_color || 'body'
      const textColorClass = `text-${textColorVal}`
      // Return
      return [
        bgColorClass,
        textColorClass,
      ]
    },
    // Header Image
    headerImageUrl() {
      const { pageObj } = this
      const { header_image } = pageObj
      const { sizes, url } = header_image || {}
      const { large } = sizes || {}
      return large || url
    },
    // Heading
    headingText() {
      const { pageObj } = this
      const { heading_text, title } = pageObj
      return heading_text || title
    },
    // Main CTA
    mainCallToActionObj() {
      const { pageObj } = this
      const { main_call_to_action } = pageObj
      const { btn_text, url } = main_call_to_action || {}
      if (!(url && btn_text)) { return }
      return main_call_to_action
    },
    // Sections
    sectionsArr() {
      const { pageObj } = this
      const { sections } = pageObj
      const validSections = sections || []
      return validSections.map((sectionObj) => {
        const { cards } = sectionObj || {}
        const validCards = cards || []
        validCards.map((cardObj) => {
          const { post } = cardObj
          const { url, slug, type, children } = post || {}
          // Has URL
          const hasUrl = url
          if (hasUrl) { return cardObj }
          const isContent = ['group', 'panorama', 'gallery', 'video', 'article'].includes(type)
          const isGroupPano = type === 'group'
          const isSinglePano = type === 'panorama'
          const isPano = isGroupPano || isSinglePano
          // Route
          const routeTo = { params: {} }
          // Route name
          routeTo.name = isPano ? 'direct-panorama' : isContent ? 'direct-media' : false
          // Params
          if (isSinglePano) {
            routeTo.params.media_id = 'single'
            routeTo.params.panorama_id = slug
          } else if (isContent) {
            routeTo.params.media_id = slug
            if (isGroupPano) {
              const validItemChildrenArr = children || []
              const firstChildObj = validItemChildrenArr[0] || {}
              routeTo.params.panorama_id = firstChildObj.slug || 'false'
            }
          }
          post.routeTo = routeTo
          // return
          return cardObj
        })

        return sectionObj
      })
    },
    // Valid
    isValid() {
      const { directPagesEnabled, pageObj } = this
      const { id } = pageObj
      return directPagesEnabled && id
    }
  },

  created() {
    this.$store.commit('pageMeta', { pageTitle: this.headingText })
  },

  beforeMount() {
    const { isValid } = this
    if (!isValid) {
      this.$router.replace({ name: 'home' })
    }
  },

  methods: {
  }
}
</script>
